<template>
  <Teleport to="body">
    <Transition name="filter-modal">
      <div v-if="show" class="fixed inset-0 z-50 flex items-end sm:items-center justify-center" @keydown.escape="emit('close')">
        <!-- Backdrop -->
        <div class="absolute inset-0 bg-black/50 filter-modal-backdrop" @click="emit('close')"></div>

        <!-- Modal panel -->
        <div class="relative bg-white w-full sm:max-w-lg sm:rounded-2xl rounded-t-2xl max-h-[85vh] flex flex-col z-10 filter-modal-panel">
          <!-- Header -->
          <div class="flex items-center justify-between px-6 py-4 border-b border-neutral-light flex-none">
            <h3 class="font-title font-bold text-lg text-primary-dark">{{ labels.filters }}</h3>
            <button
              @click="emit('close')"
              class="w-8 h-8 rounded-full hover:bg-neutral-light flex items-center justify-center transition"
            >
              <i class="ph ph-x text-xl text-neutral-dark"></i>
            </button>
          </div>

          <!-- Body (scrollable) -->
          <div class="flex-1 overflow-y-auto px-6 py-5 space-y-6">
            <!-- Zone -->
            <div>
              <label class="text-xs font-bold text-neutral-dark uppercase tracking-wide mb-2 block">
                {{ labels.zone }}
              </label>
              <select
                :value="zoneId"
                class="w-full px-3 py-2.5 border border-neutral-light rounded-xl text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/30"
                @change="emit('update:zoneId', $event.target.value ? Number($event.target.value) : null)"
              >
                <option :value="''">{{ labels.all_zones }}</option>
                <option v-for="zone in zones" :key="zone.id" :value="zone.id">{{ zone.name }}</option>
              </select>
            </div>

            <hr class="border-neutral-light" />

            <!-- Category -->
            <div>
              <label class="text-xs font-bold text-neutral-dark uppercase tracking-wide mb-2 block">
                {{ labels.category }}
              </label>
              <select
                :value="categoryId"
                class="w-full px-3 py-2.5 border border-neutral-light rounded-xl text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/30"
                @change="emit('update:categoryId', $event.target.value ? Number($event.target.value) : null)"
              >
                <option :value="''">{{ labels.all_categories }}</option>
                <option
                  v-for="cat in categories"
                  :key="cat.id"
                  :value="cat.id"
                >
                  {{ cat.name }}
                </option>
              </select>
            </div>

            <hr class="border-neutral-light" />

            <!-- Price Range -->
            <div>
              <label class="text-xs font-bold text-neutral-dark uppercase tracking-wide mb-3 block">
                {{ labels.price_range }}
              </label>
              <Range
                :min="priceRange.min"
                :max="priceRange.max"
                :initial-min="priceMin"
                :initial-max="priceMax"
                @min-changed="emit('update:priceMin', $event)"
                @max-changed="emit('update:priceMax', $event)"
              />
            </div>

            <hr class="border-neutral-light" />

            <!-- Bedrooms -->
            <div>
              <label class="text-xs font-bold text-neutral-dark uppercase tracking-wide mb-3 block">
                {{ labels.bedrooms }}
              </label>
              <div class="flex gap-2 flex-wrap">
                <button
                  v-for="opt in stepperOptions"
                  :key="'bed-' + opt.value"
                  class="px-4 py-2 rounded-full text-sm font-bold border transition"
                  :class="bedroomsMin === opt.value
                    ? 'bg-primary-dark text-white border-primary-dark'
                    : 'bg-white text-neutral-dark border-neutral-light hover:border-primary-dark'"
                  @click="emit('update:bedroomsMin', opt.value)"
                >
                  {{ opt.label }}
                </button>
              </div>
            </div>

            <!-- Bathrooms -->
            <div>
              <label class="text-xs font-bold text-neutral-dark uppercase tracking-wide mb-3 block">
                {{ labels.bathrooms }}
              </label>
              <div class="flex gap-2 flex-wrap">
                <button
                  v-for="opt in stepperOptions"
                  :key="'bath-' + opt.value"
                  class="px-4 py-2 rounded-full text-sm font-bold border transition"
                  :class="bathroomsMin === opt.value
                    ? 'bg-primary-dark text-white border-primary-dark'
                    : 'bg-white text-neutral-dark border-neutral-light hover:border-primary-dark'"
                  @click="emit('update:bathroomsMin', opt.value)"
                >
                  {{ opt.label }}
                </button>
              </div>
            </div>

            <!-- Parking -->
            <div>
              <label class="text-xs font-bold text-neutral-dark uppercase tracking-wide mb-3 block">
                {{ labels.parking }}
              </label>
              <div class="flex gap-2 flex-wrap">
                <button
                  v-for="opt in stepperOptions"
                  :key="'park-' + opt.value"
                  class="px-4 py-2 rounded-full text-sm font-bold border transition"
                  :class="parkingMin === opt.value
                    ? 'bg-primary-dark text-white border-primary-dark'
                    : 'bg-white text-neutral-dark border-neutral-light hover:border-primary-dark'"
                  @click="emit('update:parkingMin', opt.value)"
                >
                  {{ opt.label }}
                </button>
              </div>
            </div>
          </div>

          <!-- Footer -->
          <div class="flex items-center justify-between px-6 py-4 border-t border-neutral-light flex-none">
            <button
              class="text-sm font-bold text-neutral-dark underline hover:text-primary-dark transition"
              @click="emit('clear')"
            >
              {{ labels.clear_filters }}
            </button>
            <button
              class="bg-primary-dark text-white px-6 py-2.5 rounded-full font-bold text-sm hover:bg-primary-light transition"
              @click="emit('apply')"
            >
              {{ labels.show_results }}
            </button>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { watch, computed, onMounted, onUnmounted } from 'vue';
import Range from '@website/components/Range.vue';

const props = defineProps({
  show: { type: Boolean, default: false },
  labels: { type: Object, required: true },
  categories: { type: Array, default: () => [] },
  zones: { type: Array, default: () => [] },
  categoryId: { type: [Number, null], default: null },
  zoneId: { type: [Number, null], default: null },
  bedroomsMin: { type: [Number, null], default: null },
  bathroomsMin: { type: [Number, null], default: null },
  parkingMin: { type: [Number, null], default: null },
  priceRange: { type: Object, default: () => ({ min: 0, max: 1000000 }) },
  priceMin: { type: Number, default: null },
  priceMax: { type: Number, default: null },
});

const emit = defineEmits([
  'update:zoneId',
  'update:categoryId',
  'update:bedroomsMin',
  'update:bathroomsMin',
  'update:parkingMin',
  'update:priceMin',
  'update:priceMax',
  'apply',
  'clear',
  'close',
]);

const stepperOptions = computed(() => [
  { value: null, label: props.labels.any },
  { value: 1, label: '1+' },
  { value: 2, label: '2+' },
  { value: 3, label: '3+' },
  { value: 4, label: '4+' },
  { value: 5, label: '5+' },
]);

// Body scroll lock
watch(() => props.show, (val) => {
  document.body.style.overflow = val ? 'hidden' : '';
});

// Escape key
function onKeydown(e) {
  if (e.key === 'Escape' && props.show) {
    emit('close');
  }
}

onMounted(() => document.addEventListener('keydown', onKeydown));
onUnmounted(() => {
  document.removeEventListener('keydown', onKeydown);
  document.body.style.overflow = '';
});
</script>

<style>
.filter-modal-enter-active,
.filter-modal-leave-active {
  transition: opacity 0.25s ease;
}

.filter-modal-enter-active .filter-modal-panel,
.filter-modal-leave-active .filter-modal-panel {
  transition: transform 0.25s ease;
}

.filter-modal-enter-from,
.filter-modal-leave-to {
  opacity: 0;
}

.filter-modal-enter-from .filter-modal-panel {
  transform: translateY(100%);
}

.filter-modal-leave-to .filter-modal-panel {
  transform: translateY(100%);
}

@media (min-width: 640px) {
  .filter-modal-enter-from .filter-modal-panel {
    transform: scale(0.95) translateY(0);
  }
  .filter-modal-leave-to .filter-modal-panel {
    transform: scale(0.95) translateY(0);
  }
}
</style>
